import {
  toWSDateString,
  WSButton,
  WSFlexBox,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import { IInvoice, InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { RouteInvoiceDetails } from "../../../../Invoices/screens/RouteInvoiceDetails";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryInvoicesFiltered } from "../../../../query/payments/queries";
import { ButtonDownloadCsv } from "../../../../shared/components/ButtonDownloadCsv";

type Props = {
  year: number;
  clientId: string;
  payerName: string;
};

export const Payments: React.FC<Props> = ({ year, clientId, payerName }) => {
  const queryInvoices = useQueryInvoicesFiltered({
    clientId,
    status: InvoiceStatus.Paid,
    "events.paidAt": {
      ">=": new Date(Date.UTC(year, 0, 1)),
      "<": new Date(Date.UTC(year + 1, 0, 1))
    }
  });

  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  return (
    <>
      <Route
        path={match.path + "/payments/:invoiceId"}
        component={RouteInvoiceDetails}
      />

      <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
        <WSText weight="medium">Invoices</WSText>

        {queryInvoices.data && queryInvoices.data.length > 0 && (
          <ButtonDownloadCsv
            getData={async () => {
              const invoices = queryInvoices.data || [];
              return invoices.map(invoice => ({
                "Invoice Number": invoice.invoiceNumber,
                "Non-employee compensation": invoice.amount,
                "Paid Date": invoice.events.paidAt
              }));
            }}
            fileName={`${payerName} Tax Form Invoices`}
            customTrigger={<WSButton.Link>Download CSV</WSButton.Link>}
          />
        )}
      </WSFlexBox.CenterY>
      <WSQueries queries={{ queryInvoices }}>
        {({ queryInvoicesData: invoices }) => {
          return (
            <>
              <WSTable<IInvoice>
                tableData={invoices.map(invoice => ({
                  id: invoice.invoiceId,
                  data: invoice
                }))}
                onRowClick={({ data: invoice }) => {
                  history.push(
                    location.pathname + "/payments/" + invoice.invoiceId,
                    {
                      backPath: location.pathname
                    }
                  );
                }}
                columns={[
                  {
                    config: {
                      gridTemplateSizeMax: "1fr",
                      header: {
                        text: `Invoices paid in ${year}`
                      }
                    },
                    renderFunction: ({ data: payable }) => (
                      <WSTableCell
                        avatar={{
                          type: "icon",
                          icon: "check-double",
                          color: "green500",
                          colorBackground: "green50"
                        }}
                        text={payable.invoiceNumber}
                      />
                    )
                  },
                  {
                    config: {
                      gridTemplateSizeMax: "1fr",
                      justify: "start",
                      header: {
                        text: "Non-employee compensation"
                      }
                    },
                    renderFunction: ({ data: payable }) => (
                      <WSTableCell
                        text={formatMoney(payable.amount, payable.currency)}
                        secondaryText={`Paid on ${toWSDateString(
                          payable.events.paidAt,
                          "monthDate"
                        )}`}
                      />
                    )
                  }
                ]}
              />
              {invoices.length === 0 && (
                <WSText.ParagraphSm align="center" color="gray400" mt="M">
                  No invoices found
                </WSText.ParagraphSm>
              )}
            </>
          );
        }}
      </WSQueries>
    </>
  );
};
